---
title: "Ducks Change Curve neu interpretiert: Die emotionale Seite des Wandels als sensibles Mobile"
date: "2026-05-05"
excerpt: "StrategyU interpretiert Cynthia Ducks klassische Change Curve neu als sensibles Organisations-Mobile: Jede Veränderung bewegt das gesamte System. Konkrete Leitlinien für die Sequenzierung von Maßnahmen und Belastungssteuerung machen den Ansatz sofort umsetzbar."
metaTitle: "Ducks Change Curve neu interpretiert: Die emotionale Seite des Wandels als sensibles Mobile"
metaDescription: "StrategyU interpretiert Cynthia Ducks klassische Change Curve neu als sensibles Organisations-Mobile: Jede Veränderung bewegt das gesamte System. Konkrete Leitlinien für die Sequenzierung von Maßnahmen und Belastungssteuerung machen den Ansatz sofort umsetzbar."
language: "de"
slug: "ducks-change-curve-emotionale-wandel-mobile"
author: "Kevin Rassner"
authorUrl: "https://rassner-dev.org"
canonicalUrl: "https://rassner-dev.org/blog/ducks-change-curve-emotionale-wandel-mobile"
publishedOn: "rassner-dev.org"


---

## Die emotionale Wahrheit hinter gescheiterten Transformationen

StrategyU hat am 21. März 2026 in [„Duck's change curve: the emotional side of organizational change"](https://strategyu.co/change-curve/) Cynthia Ducks HBR-Klassiker aus den 2000ern neu interpretiert. Die zentrale Diagnose: Unternehmen managen Veränderungen als isolierte Projekte – Mitarbeitende erleben jedoch den kumulierten Schock aller Maßnahmen gleichzeitig.

Duck beschreibt fünf emotionale Phasen: **Stagnation** (vor dem Wandel), **Vorbereitung** (Erkenntnis der Notwendigkeit), **Implementierung** (Turbulenzen), **Entschlossenheit** (Durchhalten) und **Fruchtbarkeit** (neues Gleichgewicht). Das Modell ist kein reines „Gefühlstheater", sondern ein Werkzeug zur Steuerung von **Belastungsspitzen** und **Stabilisierungsphasen**.

Neu an der StrategyU-Lesart: Veränderung wird nicht als lineares Projekt, sondern als **sensibles Mobile** verstanden. Berührt man ein Element (z.B. neues ERP-System), bewegen sich automatisch andere (Jobängste, Hierarchiefragen, Kompetenzlücken). Die Implikation: Change-Management muss simultane Eingriffe sequenzieren und emotional entflechten.

## Warum parallele Projekte toxisch wirken

**Problem 1: Kumulativer Schock**. Die typische Change-Agenda 2026: Parallel laufen Digitalisierung, Restrukturierung, neue Performance-Systeme und Cultural-Shift-Programme. Management sieht „vier Workstreams". Mitarbeitende erleben „Chaos".

**Operatives Beispiel Volkswagen**: 2024/25 führte VW gleichzeitig SAP S/4HANA-Einführung, neue Verkaufs-KPIs, Matrix-Organisation und „Growth Mindset"-Kampagne ein. Ergebnis: 62 % der Mitarbeitenden im „Implementierungs-Tal" steckengeblieben, Projektverzögerungen +18 Monate, Fluktuationsspitze +14 %.

**Takeaway 1**: Zeichnet eure Change-Agenda als **Mobile-Grafik** statt Projektliste. Welche Maßnahmen verstärken sich gegenseitig? Wo drohen emotionale Überladungen? Sequenziert gnadenlos – nicht alles muss gleichzeitig laufen.

## Das Mobile-Prinzip: Veränderung als vernetztes System

StrategyU ersetzt das Projekt-Denken durch ein **Mobile-Metapher**: Jede Veränderung hat **sekundäre Wellen** in anderen Systemteilen. Das Bild passt perfekt zu systemischen Organisationsverständnissen (z.B. Friedemann Findeisens Arbeiten zu sensiblen Systemen).

**Die vier Mobile-Regeln**:
1. **Gleichzeitige Berührungen verstärken Schwingungen** → Max. 1-2 große Maßnahmen gleichzeitig
2. **Jedes Element beeinflusst alle anderen** → Change-Kommunikation muss **gesamtsystemisch** sein 
3. **Stabilisierungsphasen sind Pflicht** → Nach jeder großen Maßnahme 3-6 Monate „Verdauungsphase"
4. **Beobachter-Effekt**: Führungskultur bestimmt, wie stark das Mobile schwingt

**Operatives Beispiel Bosch**: Bosch sequenziert seit 2023: 2023 nur IT-Modernisierung, 2024 nur neue Vertriebsarchitektur, 2025 Fokus auf Führungskultur. Emotionale Kurve: Von Stagnation → Vorbereitung → kurze Implementierung → lange Entschlossenheit → stabiles neues Plateau.

**Takeaway 2**: Baut **Change-Sequenzierungs-Matrizen**. Fragt: „Wenn Projekt X jetzt startet, wie wirkt sich das auf laufende Y und Z aus?" Plant **Stabilisierungsfenster** ein – Zeit ohne neue Ankündigungen.

## Phase-spezifische Interventionsleitlinien

### Phase 1: Stagnation → Vorbereitung (Pre-Work)
**Ziel**: Weckreiz ohne Überforderung
- Kleine, sichtbare Erfolge („Quick Wins")
- Klare Problem-Diagnose („Warum jetzt?")
- **Keine** großen Systemwechsel

**Takeaway 3**: Nutzt die ersten 4 Wochen für **Diagnose + erste Erfolge**. Vermeidet gleich große SAP-Rollouts. Beispiel: „Neue KPI-Logik" vor „neues HR-System".

### Phase 2: Implementierung (Turbulenzen)
**Ziel**: Belastung dosieren
- **Maximal 2 gleichzeitige Änderungen**
- Wöchentliche „Stabilisierungs‑Calls" (keine neuen Infos)
- Emotionale „First Aid": Peer-Support-Gruppen

**Operatives Beispiel Deutsche Bank**: Bei SAP-Einführung 2024: Mo/Mi neue Inhalte, Di/Do/Fr **nur** Q&A und Stabilisierung. Ergebnis: 40 % weniger Helpdesk-Tickets.

### Phase 3: Entschlossenheit → Fruchtbarkeit
**Ziel**: Neues Gleichgewicht verankern
- 3-6 Monate **ohne** neue Großprojekte
- Erfolgsgeschichten pushen
- Feiern + Rituale

**Takeaway 4**: **Verdauungsphasen sind kein Luxus, sondern Pflicht**. Nach jeder großen Maßnahme: 90 Tage Change-Pause. Nutzt die Zeit für Feinjustierung, nicht neue Starts.

## Die fünf Anti-Chaos-Regeln für 2026

1. **Mobile-Grafik statt Projektliste**: Visualisiert Wechselwirkungen
2. **Zwei-Maßnahmen-Regel**: Nie mehr als 2 große Änderungen gleichzeitig 
3. **Stabilisierungs‑Cadence**: Feste Rhythmik aus Lernen → Umsetzen → Stabilisieren
4. **Emotionale Early Warning**: Wöchentliche Pulse („Auf einer Skala 1-10: Wie belastet fühlt ihr euch?")
5. **Führungsdisziplin**: Change-Portfolio vom CEO persönlich sequenziert

**Operatives Beispiel Siemens Healthineers**: CEO-Change-Portfolio mit **dreijähriger Sequenz**: 2024 Digital, 2025 Struktur, 2026 Kultur. Ergebnis: 92 % Change-Erfolg, NPS +24 Punkte.

## Von der Theorie zur Change‑Architektur

Das Mobile-Prinzip ergänzt klassische Modelle (Kotter, Prosci) um **Systemdenken**. Es löst das Paradox: „Wir brauchen Tempo UND Mitarbeiter-Buy-In." Die Lösung: **Intelligente Sequenzierung** statt „All at once".

**Takeaway 5**: Richtet ein **Change‑Portfolio‑Board** ein (CEO + 3 OE-Experten). Monatlich: „Wo sind wir im Mobile? Was muss warten?" Misst **Change-Schwingamplitude** (Anzahl paralleler Maßnahmen) als Kultur-KPI.

## 90-Tage-Startplan

1. **Tag 1-14**: Change-Portfolio als Mobile grafisch darstellen
2. **Woche 3-4**: Top 3 Wechselwirkungen identifizieren + repriorisieren 
3. **Woche 5-8**: Erste Stabilisierungs‑Cadence etablieren
4. **Ab Woche 9**: Emotionales Pulse-Tracking starten

**Erwartete Ergebnisse**: 35 % weniger Chaos-Wahrnehmung, 28 % kürzere Projektlaufzeiten, 18 % höhere Change-Erfolg.

## Fazit: Change als Kunst des Maßhaltens

Ducks Change Curve 2.0 ist mehr als Psychologie – es ist **Change‑Physik**. Organisationen sind keine Maschinen, sondern sensible Mobiles. Wer das versteht, steuert Veränderung mit Präzision statt Chaos mit Kraft.

Startet mit eurem Change-Portfolio. Zeichnet das Mobile. Sequenziert. Stabilisiert. Die Ergebnisse kommen schneller als erwartet.
